import { useCallback } from 'react';
import { useHistory } from 'react-router';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';
import ApplicationApplicantInterface from '@api/requests/applications/interfaces/ApplicationApplicantInterface.interface';

import { showToast } from '@app/providers/ToastProvider';
import { ApplicationFormQuestion } from '@app/redux/applicationForm/applicationForm.interfaces';
import { store } from '@app/redux/store';
import RoutePaths from '@app/routes/paths';
import useGenerateRoutePath from '@app/routes/paths/useGenerateRoutePath';

import useFetchApplicationForm from '@hooks/useFetchApplicationForm';

import * as environment from '@utils/environment';
import { isEnableSSO } from '@utils/environment';
import { JUNE_1_2026_4PM_EASTERN_MS } from '@utils/formatDate';

function checkVerifyDocuments({
	applicantType,
	applicants,
}: {
	applicantType: string;
	applicants: ApplicationApplicantInterface[];
}) {
	return Boolean(
		applicants.filter(
			({ type, latest_background_check }) => type === applicantType && latest_background_check.status === 'success',
		).length,
	);
}

enum QUESTIONS {
	ACCOUNT_TYPE = 10451,
	INITIAL_FUNDING_AMOUNT = 19522,
	ANNUAL_INCOME = 15054,
	LIQUID_NET_WORTH = 15056,
	TOTAL_NET_WORTH = 15055,
	TYPE_OF_MARGIN_ACCOUNT = 14070,
	LEVEL_4_SPECULATION = 15197,
	LEVEL_5_SPECULATION = 15868,
	RISK_TOLERANCE = 19518,
	US_CITIZENSHIP = 10373,
	US_CURRENT_ADDRESS = 10443,
	IS_YOUR_CURRENT_ADDRESS_THE_SAME_AS_YOUR_MAILING_ADDRESS = 10847,
	US_MAILING_ADDRESS = 10848,
	US_PERSON = 16634,
	TRADING_PLATFORM = 15956,
	FIRM_ASSOCIATION = 10408,
	BROKER_ASSOCIATION = 10438,
	PUBLIC_COMPANY_ASSOCIATION = 10440,
	POLITICALLY_EXPOSED = 32161,
	JOINT_IS_YOUR_CURRENT_ADDRESS_THE_SAME_AS_YOUR_MAILING_ADDRESS = 15006,
	JOINT_US_CITIZENSHIP = 14985,
	JOINT_US_PERSON = 19909,
	JOINT_CURRENT_ADDRESS = 14996,
	JOINT_MAILING_ADDRESS = 15007,
	JOINT_FIRM_ASSOCIATION = 32164,
	JOINT_BROKER_ASSOCIATION = 15024,
	JOINT_PUBLIC_COMPANY_ASSOCIATION = 15022,
	JOINT_ACCOUNT_POLITICALLY_EXPOSED = 15019,
	OPTION_LEVEL_CASH = 10468,
	OPTION_LEVEL_MARGIN = 15048,
	TRADE_OPTIONS = 10466,
	INVESTMENT_OBJECTIVES_CAPITAL_1 = 15151,
	INVESTMENT_OBJECTIVES_CAPITAL_2 = 15196,
	INVESTMENT_OBJECTIVES_CAPITAL_3 = 15871,
	INVESTMENT_OBJECTIVES_CAPITAL_4 = 15872,
	INVESTMENT_OBJECTIVES_NO_OPTIONS = 32177,
	INVESTMENT_OBJECTIVES_OPTION_4 = 15869,
	INVESTMENT_OBJECTIVES_OPTION_5 = 15870,
	CUSTOMER_TYPE = 10453,
	PROMOTIONAL_CODE = 12425,
}

enum ANSWERS {
	ACCOUNT_TYPE_MARGIN_OPTION = 10708,
	ACCOUNT_TYPE_CASH_OPTION = 10707,
	ANNUAL_INCOME_LESS_THAN_TEN_THOUSAND_OPTION = 32271,
	ANNUAL_INCOME_LESS_THAN_FIFTEEN_THOUSAND_OPTION = 30405,
	ANNUAL_INCOME_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION = 15670,
	ANNUAL_INCOME_LESS_THAN_THIRTYTHREE_THOUSAND_OPTION = 15671,
	ANNUAL_INCOME_LESS_THAN_FORTY_THOUSAND_OPTION = 15672,
	ANNUAL_INCOME_LESS_THAN_FIFTY_THOUSAND_OPTION = 15673,
	ANNUAL_INCOME_LESS_THAN_SIXTY_THOUSAND_OPTION = 15674,
	ANNUAL_INCOME_LESS_THAN_SIXTY_FIVE_THOUSAND_OPTION = 15675,
	ANNUAL_INCOME_LESS_THAN_SEVENTY_THOUSAND_OPTION = 15676,
	ANNUAL_INCOME_LESS_THAN_EIGHTY_THOUSAND_OPTION = 15677,
	LIQUID_NET_WORTH_LESS_THAN_TEN_THOUSAND_OPTION = 32272,
	LIQUID_NET_WORTH_LESS_THAN_FIFTEEN_THOUSAND_OPTION = 30408,
	LIQUID_NET_WORTH_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION = 15692,
	LIQUID_NET_WORTH_LESS_THAN_THIRTYTHREE_THOUSAND_OPTION = 15694,
	LIQUID_NET_WORTH_LESS_THAN_FORTY_THOUSAND_OPTION = 15696,
	LIQUID_NET_WORTH_LESS_THAN_FIFTY_THOUSAND_OPTION = 15699,
	LIQUID_NET_WORTH_LESS_THAN_SIXTY_THOUSAND_OPTION = 15701,
	LIQUID_NET_WORTH_LESS_THAN_SIXTY_FIVE_THOUSAND_OPTION = 15702,
	LIQUID_NET_WORTH_LESS_THAN_SEVENTY_THOUSAND_OPTION = 15704,
	LIQUID_NET_WORTH_LESS_THAN_EIGHTY_THOUSAND_OPTION = 15706,
	LIQUID_NET_WORTH_LESS_THAN_NINETY_THOUSAND_OPTION = 15708,
	LIQUID_NET_WORTH_LESS_THAN_ONE_HUNDRED_THOUSAND_OPTION = 15711,
	TOTAL_NET_WORTH_LESS_THAN_TEN_THOUSAND_OPTION = 32273,
	TOTAL_NET_WORTH_LESS_THAN_FIFTEEN_THOUSAND_OPTION = 30412,
	TOTAL_NET_WORTH_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION = 15693,
	TOTAL_NET_WORTH_LESS_THAN_THIRTYTHREE_THOUSAND_OPTION = 15695,
	TOTAL_NET_WORTH_LESS_THAN_FORTY_THOUSAND_OPTION = 15697,
	TOTAL_NET_WORTH_LESS_THAN_FIFTY_THOUSAND_OPTION = 15698,
	TOTAL_NET_WORTH_LESS_THAN_SIXTY_THOUSAND_OPTION = 15700,
	TOTAL_NET_WORTH_LESS_THAN_SIXTY_FIVE_THOUSAND_OPTION = 15703,
	TOTAL_NET_WORTH_LESS_THAN_SEVENTY_THOUSAND_OPTION = 15705,
	TOTAL_NET_WORTH_LESS_THAN_EIGHTY_THOUSAND_OPTION = 15707,
	TOTAL_NET_WORTH_LESS_THAN_NINETY_THOUSAND_OPTION = 15709,
	TOTAL_NET_WORTH_LESS_THAN_ONE_HUNDRED_THOUSAND_OPTION = 15710,
	TOTAL_NET_WORTH_LESS_THAN_ONE_HUNDRED_TEN_THOUSAND_OPTION = 15712,
	TOTAL_NET_WORTH_LESS_THAN_ONE_HUNDRED_TWENTY_FIVE_THOUSAND_OPTION = 15715,
	TOTAL_NET_WORTH_LESS_THAN_ONE_HUNDRED_FIFTY_THOUSAND_OPTION = 15716,
	TYPE_OF_MARGIN_ACCOUNT_2_OPTION = 14238,
	TYPE_OF_MARGIN_ACCOUNT_1_OPTION = 14237,
	DAY_TRADING_SPECULATION_OPTION = 16873,
	IS_YOUR_CURRENT_ADDRESS_THE_SAME_AS_YOUR_MAILING_ADDRESS_NO_OPTION = 11127,
	LEVEL_4_SPECULATION_OPTION = 16873,
	LEVEL_5_SPECULATION_OPTION = 17679,
	RISK_TOLERANCE_HIGH_RISK_OPTION = 20865,
	US_CITIZENSHIP_NO_OPTION = 10678,
	US_PERSON_NO_OPTION = 18411,
	US_PERSON_YES_OPTION = 18410,
	FIRM_ASSOCIATION_YES_OPTION = 10697,
	BROKER_ASSOCIATION_YES_OPTION = 10699,
	PUBLIC_COMPANY_ASSOCIATION_YES_OPTION = 10701,
	POLITICALLY_EXPOSED_YES_OPTION = 32267,
	US_CURRENT_ADDRESS_NO_OPTION = 10706,
	US_MAILING_ADDRESS_NO_OPTION = 11129,
	TRADING_PLATFORM_LIGHTSPEEDCONNECT_OPTION = 29627,
	TRADING_PLATFORM_STERLINGTRADERPRO_OPTION = 26950,
	TRADING_PLATFORM_LIGHTSPEEDTRADER_OPTION = 17873,
	TRADING_PLATFORM_DASTRADERPRO_OPTION = 29628,
	TRADING_PLATFORM_REALTICK_OPTION = 30394,
	TRADING_PLATFORM_SILEXX_OPTION = 30395,
	TRADING_VIEW_OPTION = 32266,
	TRADING_LIGHTSPEED_WEB_MOBILE_OPTION = 27207,
	JOINT_US_PERSON_NO_OPTION = 21182,
	JOINT_US_PERSON_YES_OPTION = 21181,
	JOINT_US_CITIZENSHIP_NO_OPTION = 15572,
	JOINT_CURRENT_ADDRESS_NO_OPTION = 15589,
	JOINT_MAILING_ADDRESS_NO_OPTION = 15595,
	JOINT_FIRM_ASSOCIATION_YES_OPTION = 32269,
	JOINT_BROKER_ASSOCIATION_YES_OPTION = 15604,
	JOINT_PUBLIC_COMPANY_ASSOCIATION_YES_OPTION = 15602,
	JOINT_ACCOUNT_POLITICALLY_EXPOSED_YES_OPTION = 15600,
	JOINT_IS_YOUR_CURRENT_ADDRESS_THE_SAME_AS_YOUR_MAILING_ADDRESS_NO_OPTION = 15593,
	OPTION_LEVEL_CASH_TWO_OPTION = 10762,
	OPTION_LEVEL_MARGIN_TWO_OPTION = 15635,
	OPTION_LEVEL_MARGIN_THREE_OPTION = 15636,
	OPTION_LEVEL_MARGIN_FOUR_OPTION = 15639,
	OPTION_LEVEL_MARGIN_FIVE_OPTION = 15641,
	TRADE_OPTIONS_YES_OPTION = 10755,
	YOUR_INVESTMENT_OBJECTIVES_CAPITAL_1_OPTION = 16636,
	YOUR_INVESTMENT_OBJECTIVES_CAPITAL_2_OPTION = 16872,
	YOUR_INVESTMENT_OBJECTIVES_CAPITAL_3_OPTION = 17688,
	YOUR_INVESTMENT_OBJECTIVES_CAPITAL_4_OPTION = 17694,
	YOUR_INVESTMENT_OBJECTIVES_NO_OPTIONS_OPTION = 32295,
	YOUR_INVESTMENT_OBJECTIVES_OPTION_4_OPTION = 17682,
	YOUR_INVESTMENT_OBJECTIVES_OPTION_5_OPTION = 17685,
	CUSTOMER_TYPE_JOINT_OPTION = 10712,
}

const isOptionSelected = (question: ApplicationFormQuestion, optionId: number): boolean => {
	const option = question.options.find(option => option.id === optionId);
	return option?.is_checked === true;
};

const isAnyOptionSelected = (question: ApplicationFormQuestion, optionIds: number[]): boolean => {
	return optionIds.some(optionId => isOptionSelected(question, optionId));
};

const isOnlyOptionSelected = (question: ApplicationFormQuestion, optionId: number): boolean => {
	return (
		isOptionSelected(question, optionId) &&
		question.options.filter(option => option.id !== optionId).every(option => option.is_checked === false)
	);
};

const parseNumericValue = (value: string | number): number => {
	const stripped = String(value ?? '').replace(/[^\d.]/g, '');
	const parsed = parseFloat(stripped);
	return Number.isFinite(parsed) ? parsed : 0;
};

/** Resolved form answers used by LightSpeed STP rules. All question/option IDs live in QUESTIONS and ANSWERS enums. */
interface LightSpeedFormAnswers {
	isMargin: boolean;
	isCash: boolean;
	tradeOptions: boolean;
	initialFundingLessThanTwoThousand: boolean;
	initialFundingLessThanTwoThousandFiveHundred: boolean;
	initialFundingLessThanFiveThousand: boolean;
	initialFundingLessThanTwentyFiveThousand: boolean;
	initialFundingLessThanThirtyThousand: boolean;
	initialFundingLessThanFiftyThousand: boolean;
	initialFundingLessThanSeventyFiveThousand: boolean;
	initialFundingLessThanOneHundredThousand: boolean;
	promotionalCodeLegacy: boolean;
	annualIncomeLessThanTenThousand: boolean;
	annualIncomeLessThanTwentyFiveThousand: boolean;
	annualIncomeLessThanFortyThousand: boolean;
	annualIncomeLessThanEightyThousand: boolean;
	liquidNetWorthLessThanTenThousand: boolean;
	liquidNetWorthLessThanTwentyFiveThousand: boolean;
	liquidNetWorthLessThanFortyThousand: boolean;
	liquidNetWorthLessThanFiftyThousand: boolean;
	liquidNetWorthLessThanOneHundredThousand: boolean;
	totalNetWorthLessThanTenThousand: boolean;
	totalNetWorthLessThanTwentyFiveThousand: boolean;
	totalNetWorthLessThanFiftyThousand: boolean;
	totalNetWorthLessThanSixtyFiveThousand: boolean;
	totalNetWorthLessThanOneHundredFiftyThousand: boolean;
	investmentObjectivesCapital: boolean;
	isMailingAddressDifferentFromCurrentAddress: boolean;
	isJointMailingAddressDifferentFromCurrentAddress: boolean;
	level4Speculation: boolean;
	level5Speculation: boolean;
	riskToleranceHighRisk: boolean;
	usCitizenshipNo: boolean;
	optionLevelTwoCash: boolean;
	optionLevelTwoMargin: boolean;
	optionLevelThreeMargin: boolean;
	optionLevelFourMargin: boolean;
	optionLevelFiveMargin: boolean;
	margin1: boolean;
	margin2: boolean;
	usPersonNo: boolean;
	usPersonYes: boolean;
	firmAssociationYes: boolean;
	brokerAssociationYes: boolean;
	publicCompanyAssociationYes: boolean;
	politicallyExposedYes: boolean;
	usCurrentAddressNo: boolean;
	usMailingAddressNo: boolean;
	tradingView: boolean;
	tradingLightspeedWebMobile: boolean;
	tradingLightspeedTrader: boolean;
	tradingPlatformLightspeedConnect: boolean;
	tradingPlatformSterlingTraderPro: boolean;
	tradingPlatformDastraderPro: boolean;
	tradingPlatformRealtick: boolean;
	tradingPlatformSilexx: boolean;
	isJoint: boolean;
	jointUsCitizenshipNo: boolean;
	jointUsPersonNo: boolean;
	jointUsPersonYes: boolean;
	jointCurrentAddressNo: boolean;
	jointMailingAddressNo: boolean;
	jointFirmAssociationYes: boolean;
	jointBrokerAssociationYes: boolean;
	jointPublicCompanyAssociationYes: boolean;
	jointAccountPoliticallyExposedYes: boolean;
}

function getLightSpeedFormAnswers(questions: Record<number, ApplicationFormQuestion>): LightSpeedFormAnswers {
	const q = (id: number): ApplicationFormQuestion => questions[id];
	return {
		isMargin: isOptionSelected(q(QUESTIONS.ACCOUNT_TYPE), ANSWERS.ACCOUNT_TYPE_MARGIN_OPTION),
		isCash: isOptionSelected(q(QUESTIONS.ACCOUNT_TYPE), ANSWERS.ACCOUNT_TYPE_CASH_OPTION),
		tradeOptions: isOptionSelected(q(QUESTIONS.TRADE_OPTIONS), ANSWERS.TRADE_OPTIONS_YES_OPTION),
		optionLevelTwoCash: isOptionSelected(q(QUESTIONS.OPTION_LEVEL_CASH), ANSWERS.OPTION_LEVEL_CASH_TWO_OPTION),
		optionLevelTwoMargin: isOptionSelected(q(QUESTIONS.OPTION_LEVEL_MARGIN), ANSWERS.OPTION_LEVEL_MARGIN_TWO_OPTION),
		optionLevelThreeMargin: isOptionSelected(
			q(QUESTIONS.OPTION_LEVEL_MARGIN),
			ANSWERS.OPTION_LEVEL_MARGIN_THREE_OPTION,
		),
		optionLevelFourMargin: isOptionSelected(q(QUESTIONS.OPTION_LEVEL_MARGIN), ANSWERS.OPTION_LEVEL_MARGIN_FOUR_OPTION),
		optionLevelFiveMargin: isOptionSelected(q(QUESTIONS.OPTION_LEVEL_MARGIN), ANSWERS.OPTION_LEVEL_MARGIN_FIVE_OPTION),
		initialFundingLessThanTwoThousand: parseNumericValue(q(QUESTIONS.INITIAL_FUNDING_AMOUNT)?.value) < 2000,
		initialFundingLessThanTwoThousandFiveHundred: parseNumericValue(q(QUESTIONS.INITIAL_FUNDING_AMOUNT)?.value) < 2500,
		initialFundingLessThanFiveThousand: parseNumericValue(q(QUESTIONS.INITIAL_FUNDING_AMOUNT)?.value) < 5000,
		initialFundingLessThanTwentyFiveThousand: parseNumericValue(q(QUESTIONS.INITIAL_FUNDING_AMOUNT)?.value) < 25000,
		initialFundingLessThanThirtyThousand: parseNumericValue(q(QUESTIONS.INITIAL_FUNDING_AMOUNT)?.value) < 30000,
		initialFundingLessThanFiftyThousand: parseNumericValue(q(QUESTIONS.INITIAL_FUNDING_AMOUNT)?.value) < 50000,
		initialFundingLessThanSeventyFiveThousand: parseNumericValue(q(QUESTIONS.INITIAL_FUNDING_AMOUNT)?.value) < 75000,
		initialFundingLessThanOneHundredThousand: parseNumericValue(q(QUESTIONS.INITIAL_FUNDING_AMOUNT)?.value) < 100000,
		promotionalCodeLegacy: String(q(QUESTIONS.PROMOTIONAL_CODE)?.value ?? '').toLowerCase() === 'legacy',
		annualIncomeLessThanTenThousand: isAnyOptionSelected(q(QUESTIONS.ANNUAL_INCOME), [
			ANSWERS.ANNUAL_INCOME_LESS_THAN_TEN_THOUSAND_OPTION,
		]),
		annualIncomeLessThanTwentyFiveThousand: isAnyOptionSelected(q(QUESTIONS.ANNUAL_INCOME), [
			ANSWERS.ANNUAL_INCOME_LESS_THAN_TEN_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_FIFTEEN_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION,
		]),
		isMailingAddressDifferentFromCurrentAddress: isOptionSelected(
			q(QUESTIONS.IS_YOUR_CURRENT_ADDRESS_THE_SAME_AS_YOUR_MAILING_ADDRESS),
			ANSWERS.IS_YOUR_CURRENT_ADDRESS_THE_SAME_AS_YOUR_MAILING_ADDRESS_NO_OPTION,
		),
		isJointMailingAddressDifferentFromCurrentAddress: isOptionSelected(
			q(QUESTIONS.JOINT_IS_YOUR_CURRENT_ADDRESS_THE_SAME_AS_YOUR_MAILING_ADDRESS),
			ANSWERS.JOINT_IS_YOUR_CURRENT_ADDRESS_THE_SAME_AS_YOUR_MAILING_ADDRESS_NO_OPTION,
		),
		investmentObjectivesCapital:
			isOnlyOptionSelected(
				q(QUESTIONS.INVESTMENT_OBJECTIVES_CAPITAL_1),
				ANSWERS.YOUR_INVESTMENT_OBJECTIVES_CAPITAL_1_OPTION,
			) ||
			isOnlyOptionSelected(
				q(QUESTIONS.INVESTMENT_OBJECTIVES_CAPITAL_2),
				ANSWERS.YOUR_INVESTMENT_OBJECTIVES_CAPITAL_2_OPTION,
			) ||
			isOnlyOptionSelected(
				q(QUESTIONS.INVESTMENT_OBJECTIVES_CAPITAL_3),
				ANSWERS.YOUR_INVESTMENT_OBJECTIVES_CAPITAL_3_OPTION,
			) ||
			isOnlyOptionSelected(
				q(QUESTIONS.INVESTMENT_OBJECTIVES_CAPITAL_4),
				ANSWERS.YOUR_INVESTMENT_OBJECTIVES_CAPITAL_4_OPTION,
			) ||
			isOnlyOptionSelected(
				q(QUESTIONS.INVESTMENT_OBJECTIVES_NO_OPTIONS),
				ANSWERS.YOUR_INVESTMENT_OBJECTIVES_NO_OPTIONS_OPTION,
			) ||
			isOnlyOptionSelected(
				q(QUESTIONS.INVESTMENT_OBJECTIVES_OPTION_4),
				ANSWERS.YOUR_INVESTMENT_OBJECTIVES_OPTION_4_OPTION,
			) ||
			isOnlyOptionSelected(
				q(QUESTIONS.INVESTMENT_OBJECTIVES_OPTION_5),
				ANSWERS.YOUR_INVESTMENT_OBJECTIVES_OPTION_5_OPTION,
			),
		annualIncomeLessThanFortyThousand: isAnyOptionSelected(q(QUESTIONS.ANNUAL_INCOME), [
			ANSWERS.ANNUAL_INCOME_LESS_THAN_TEN_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_FIFTEEN_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_THIRTYTHREE_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_FORTY_THOUSAND_OPTION,
		]),
		annualIncomeLessThanEightyThousand: isAnyOptionSelected(q(QUESTIONS.ANNUAL_INCOME), [
			ANSWERS.ANNUAL_INCOME_LESS_THAN_TEN_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_FIFTEEN_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_THIRTYTHREE_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_FORTY_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_FIFTY_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_SIXTY_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_SIXTY_FIVE_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_SEVENTY_THOUSAND_OPTION,
			ANSWERS.ANNUAL_INCOME_LESS_THAN_EIGHTY_THOUSAND_OPTION,
		]),
		liquidNetWorthLessThanTenThousand: isAnyOptionSelected(q(QUESTIONS.LIQUID_NET_WORTH), [
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_TEN_THOUSAND_OPTION,
		]),
		liquidNetWorthLessThanTwentyFiveThousand: isAnyOptionSelected(q(QUESTIONS.LIQUID_NET_WORTH), [
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_TEN_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_FIFTEEN_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION,
		]),
		liquidNetWorthLessThanFortyThousand: isAnyOptionSelected(q(QUESTIONS.LIQUID_NET_WORTH), [
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_TEN_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_FIFTEEN_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_THIRTYTHREE_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_FORTY_THOUSAND_OPTION,
		]),
		liquidNetWorthLessThanFiftyThousand: isAnyOptionSelected(q(QUESTIONS.LIQUID_NET_WORTH), [
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_TEN_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_FIFTEEN_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_THIRTYTHREE_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_FORTY_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_FIFTY_THOUSAND_OPTION,
		]),
		liquidNetWorthLessThanOneHundredThousand: isAnyOptionSelected(q(QUESTIONS.LIQUID_NET_WORTH), [
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_TEN_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_FIFTEEN_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_THIRTYTHREE_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_FORTY_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_FIFTY_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_SIXTY_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_SIXTY_FIVE_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_SEVENTY_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_EIGHTY_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_NINETY_THOUSAND_OPTION,
			ANSWERS.LIQUID_NET_WORTH_LESS_THAN_ONE_HUNDRED_THOUSAND_OPTION,
		]),
		totalNetWorthLessThanTenThousand: isAnyOptionSelected(q(QUESTIONS.TOTAL_NET_WORTH), [
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_TEN_THOUSAND_OPTION,
		]),
		totalNetWorthLessThanTwentyFiveThousand: isAnyOptionSelected(q(QUESTIONS.TOTAL_NET_WORTH), [
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_TEN_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_FIFTEEN_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION,
		]),
		totalNetWorthLessThanFiftyThousand: isAnyOptionSelected(q(QUESTIONS.TOTAL_NET_WORTH), [
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_TEN_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_FIFTEEN_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_THIRTYTHREE_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_FORTY_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_FIFTY_THOUSAND_OPTION,
		]),
		totalNetWorthLessThanSixtyFiveThousand: isAnyOptionSelected(q(QUESTIONS.TOTAL_NET_WORTH), [
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_TEN_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_FIFTEEN_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_THIRTYTHREE_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_FORTY_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_FIFTY_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_SIXTY_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_SIXTY_FIVE_THOUSAND_OPTION,
		]),
		totalNetWorthLessThanOneHundredFiftyThousand: isAnyOptionSelected(q(QUESTIONS.TOTAL_NET_WORTH), [
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_TEN_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_FIFTEEN_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_TWENTYFIVE_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_THIRTYTHREE_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_FORTY_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_FIFTY_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_SIXTY_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_SIXTY_FIVE_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_SEVENTY_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_EIGHTY_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_NINETY_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_ONE_HUNDRED_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_ONE_HUNDRED_TEN_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_ONE_HUNDRED_TWENTY_FIVE_THOUSAND_OPTION,
			ANSWERS.TOTAL_NET_WORTH_LESS_THAN_ONE_HUNDRED_FIFTY_THOUSAND_OPTION,
		]),
		margin1: isOptionSelected(q(QUESTIONS.TYPE_OF_MARGIN_ACCOUNT), ANSWERS.TYPE_OF_MARGIN_ACCOUNT_1_OPTION),
		margin2: isOptionSelected(q(QUESTIONS.TYPE_OF_MARGIN_ACCOUNT), ANSWERS.TYPE_OF_MARGIN_ACCOUNT_2_OPTION),
		level4Speculation: isOptionSelected(q(QUESTIONS.LEVEL_4_SPECULATION), ANSWERS.LEVEL_4_SPECULATION_OPTION),
		level5Speculation: isOptionSelected(q(QUESTIONS.LEVEL_5_SPECULATION), ANSWERS.LEVEL_5_SPECULATION_OPTION),
		riskToleranceHighRisk: isOptionSelected(q(QUESTIONS.RISK_TOLERANCE), ANSWERS.RISK_TOLERANCE_HIGH_RISK_OPTION),
		usCitizenshipNo: isOptionSelected(q(QUESTIONS.US_CITIZENSHIP), ANSWERS.US_CITIZENSHIP_NO_OPTION),
		usPersonNo: isOptionSelected(q(QUESTIONS.US_PERSON), ANSWERS.US_PERSON_NO_OPTION),
		usPersonYes: isOptionSelected(q(QUESTIONS.US_PERSON), ANSWERS.US_PERSON_YES_OPTION),
		firmAssociationYes: isOptionSelected(q(QUESTIONS.FIRM_ASSOCIATION), ANSWERS.FIRM_ASSOCIATION_YES_OPTION),
		brokerAssociationYes: isOptionSelected(q(QUESTIONS.BROKER_ASSOCIATION), ANSWERS.BROKER_ASSOCIATION_YES_OPTION),
		politicallyExposedYes: isOptionSelected(q(QUESTIONS.POLITICALLY_EXPOSED), ANSWERS.POLITICALLY_EXPOSED_YES_OPTION),
		publicCompanyAssociationYes: isOptionSelected(
			q(QUESTIONS.PUBLIC_COMPANY_ASSOCIATION),
			ANSWERS.PUBLIC_COMPANY_ASSOCIATION_YES_OPTION,
		),
		usCurrentAddressNo: isOptionSelected(q(QUESTIONS.US_CURRENT_ADDRESS), ANSWERS.US_CURRENT_ADDRESS_NO_OPTION),
		usMailingAddressNo: isOptionSelected(q(QUESTIONS.US_MAILING_ADDRESS), ANSWERS.US_MAILING_ADDRESS_NO_OPTION),
		tradingView: isOptionSelected(q(QUESTIONS.TRADING_PLATFORM), ANSWERS.TRADING_VIEW_OPTION),
		tradingLightspeedWebMobile: isOptionSelected(
			q(QUESTIONS.TRADING_PLATFORM),
			ANSWERS.TRADING_LIGHTSPEED_WEB_MOBILE_OPTION,
		),
		tradingLightspeedTrader: isOptionSelected(
			q(QUESTIONS.TRADING_PLATFORM),
			ANSWERS.TRADING_PLATFORM_LIGHTSPEEDTRADER_OPTION,
		),
		tradingPlatformLightspeedConnect: isOptionSelected(
			q(QUESTIONS.TRADING_PLATFORM),
			ANSWERS.TRADING_PLATFORM_LIGHTSPEEDCONNECT_OPTION,
		),
		tradingPlatformSterlingTraderPro: isOptionSelected(
			q(QUESTIONS.TRADING_PLATFORM),
			ANSWERS.TRADING_PLATFORM_STERLINGTRADERPRO_OPTION,
		),
		tradingPlatformDastraderPro: isOptionSelected(
			q(QUESTIONS.TRADING_PLATFORM),
			ANSWERS.TRADING_PLATFORM_DASTRADERPRO_OPTION,
		),
		tradingPlatformRealtick: isOptionSelected(q(QUESTIONS.TRADING_PLATFORM), ANSWERS.TRADING_PLATFORM_REALTICK_OPTION),
		tradingPlatformSilexx: isOptionSelected(q(QUESTIONS.TRADING_PLATFORM), ANSWERS.TRADING_PLATFORM_SILEXX_OPTION),
		isJoint: isOptionSelected(q(QUESTIONS.CUSTOMER_TYPE), ANSWERS.CUSTOMER_TYPE_JOINT_OPTION),
		jointUsCitizenshipNo: isOptionSelected(q(QUESTIONS.JOINT_US_CITIZENSHIP), ANSWERS.JOINT_US_CITIZENSHIP_NO_OPTION),
		jointUsPersonNo: isOptionSelected(q(QUESTIONS.JOINT_US_PERSON), ANSWERS.JOINT_US_PERSON_NO_OPTION),
		jointUsPersonYes: isOptionSelected(q(QUESTIONS.JOINT_US_PERSON), ANSWERS.JOINT_US_PERSON_YES_OPTION),
		jointCurrentAddressNo: isOptionSelected(
			q(QUESTIONS.JOINT_CURRENT_ADDRESS),
			ANSWERS.JOINT_CURRENT_ADDRESS_NO_OPTION,
		),
		jointMailingAddressNo: isOptionSelected(
			q(QUESTIONS.JOINT_MAILING_ADDRESS),
			ANSWERS.JOINT_MAILING_ADDRESS_NO_OPTION,
		),
		jointFirmAssociationYes: isOptionSelected(
			q(QUESTIONS.JOINT_FIRM_ASSOCIATION),
			ANSWERS.JOINT_FIRM_ASSOCIATION_YES_OPTION,
		),
		jointBrokerAssociationYes: isOptionSelected(
			q(QUESTIONS.JOINT_BROKER_ASSOCIATION),
			ANSWERS.JOINT_BROKER_ASSOCIATION_YES_OPTION,
		),
		jointPublicCompanyAssociationYes: isOptionSelected(
			q(QUESTIONS.JOINT_PUBLIC_COMPANY_ASSOCIATION),
			ANSWERS.JOINT_PUBLIC_COMPANY_ASSOCIATION_YES_OPTION,
		),
		jointAccountPoliticallyExposedYes: isOptionSelected(
			q(QUESTIONS.JOINT_ACCOUNT_POLITICALLY_EXPOSED),
			ANSWERS.JOINT_ACCOUNT_POLITICALLY_EXPOSED_YES_OPTION,
		),
	};
}

interface STPRuleCheckResult {
	title: string;
	status: 'yes' | 'no' | 'pass' | 'fail' | 'na';
}

interface STPRuleResult {
	title: string;
	status: 'pass' | 'fail' | 'na';
	checks: STPRuleCheckResult[];
}

interface STPCheckResult {
	isAllowed: boolean;
	rules: STPRuleResult[];
}

const createRuleResult = ({
	title,
	applies,
	blocksSTP,
	checks,
}: {
	title: string;
	applies: boolean;
	blocksSTP: boolean;
	checks: STPRuleCheckResult[];
}): STPRuleResult => ({
	title,
	status: !applies ? 'na' : blocksSTP ? 'fail' : 'pass',
	checks,
});

function getCheckStatus(
	value: boolean,
	applies: boolean,
	mode: 'noYes' | 'failPass',
): 'yes' | 'no' | 'pass' | 'fail' | 'na' {
	if (!applies) return 'na';
	if (mode === 'failPass') return value ? 'pass' : 'fail';
	return value ? 'yes' : 'no';
}

const checkOptionLevelTwo = (a: LightSpeedFormAnswers): boolean => {
	if (a.isCash) return a.optionLevelTwoCash;
	if (a.isMargin) return a.optionLevelTwoMargin;
	return false;
};

const checkOptionLevelThree = (a: LightSpeedFormAnswers): boolean => {
	if (a.isMargin) return a.optionLevelThreeMargin;
	return false;
};

const checkOptionLevelFour = (a: LightSpeedFormAnswers): boolean => {
	if (a.isMargin) return a.optionLevelFourMargin;
	return false;
};

const checkOptionLevelFive = (a: LightSpeedFormAnswers): boolean => {
	if (a.isMargin) return a.optionLevelFiveMargin;
	return false;
};

const checkOptionLevelFourSpeculation = (a: LightSpeedFormAnswers): boolean => {
	if (a.isMargin) return a.level4Speculation;
	return false;
};

const checkOptionLevelFiveSpeculation = (a: LightSpeedFormAnswers): boolean => {
	if (a.isMargin) return a.level5Speculation;
	return false;
};

const getLightSpeedRuleResults = (a: LightSpeedFormAnswers): STPRuleResult[] => [
	createRuleResult({
		title: 'Promotional Code Legacy',
		applies: true,
		blocksSTP: a.promotionalCodeLegacy,
		checks: [
			{
				title: 'Promotional Code Legacy',
				status: getCheckStatus(!a.promotionalCodeLegacy, true, 'failPass'),
			},
		],
	}),
	createRuleResult({
		title: 'Cash, Initial Funding Amount < $2,000, TradingView',
		applies: true,
		blocksSTP: a.isCash && a.initialFundingLessThanTwoThousand && a.tradingView,
		checks: [
			{
				title: 'Cash',
				status: getCheckStatus(a.isCash, true, 'noYes'),
			},
			{
				title: 'Initial Funding Amount < $2,000',
				status: getCheckStatus(a.initialFundingLessThanTwoThousand, true, 'noYes'),
			},
			{
				title: 'TradingView',
				status: getCheckStatus(a.tradingView, true, 'noYes'),
			},
		],
	}),
	createRuleResult({
		title: 'Cash, Initial Funding Amount < $2,500, Lightspeed Trader OR Lightspeed Web and Mobile',
		applies: true,
		blocksSTP:
			a.isCash &&
			a.initialFundingLessThanTwoThousandFiveHundred &&
			(a.tradingLightspeedTrader || a.tradingLightspeedWebMobile),
		checks: [
			{
				title: 'Cash',
				status: getCheckStatus(a.isCash, true, 'noYes'),
			},
			{
				title: 'Initial Funding Amount < $2,500',
				status: getCheckStatus(a.initialFundingLessThanTwoThousandFiveHundred, true, 'noYes'),
			},
			{
				title: 'Lightspeed Trader OR Lightspeed Web and Mobile',
				status: getCheckStatus(a.tradingLightspeedTrader || a.tradingLightspeedWebMobile, true, 'noYes'),
			},
		],
	}),
	createRuleResult({
		title: 'Margin 1, Initial Funding Amount < $2,500',
		applies: true,
		blocksSTP: a.isMargin && a.margin1 && a.initialFundingLessThanTwoThousandFiveHundred,
		checks: [
			{
				title: 'Margin 1',
				status: getCheckStatus(a.margin1, a.isMargin, 'noYes'),
			},
			{
				title: 'Initial Funding Amount < $2,500',
				status: getCheckStatus(a.initialFundingLessThanTwoThousandFiveHundred, true, 'noYes'),
			},
		],
	}),
	createRuleResult({
		title: 'Margin 2, Initial Funding Amount < $2,500, TradingView',
		applies: true,
		blocksSTP: a.isMargin && a.margin2 && a.initialFundingLessThanTwoThousandFiveHundred && a.tradingView,
		checks: [
			{
				title: 'Margin 2',
				status: getCheckStatus(a.margin2, a.isMargin, 'noYes'),
			},
			{
				title: 'Initial Funding Amount < $2,500',
				status: getCheckStatus(a.initialFundingLessThanTwoThousandFiveHundred, true, 'noYes'),
			},
			{
				title: 'TradingView',
				status: getCheckStatus(a.tradingView, true, 'noYes'),
			},
		],
	}),
	createRuleResult({
		title: 'Margin 2, Initial Funding Amount < $5,000, Lightspeed Trader OR Lightspeed Web and Mobile',
		applies: true,
		blocksSTP:
			a.isMargin &&
			a.margin2 &&
			a.initialFundingLessThanFiveThousand &&
			(a.tradingLightspeedTrader || a.tradingLightspeedWebMobile),
		checks: [
			{
				title: 'Margin 2',
				status: getCheckStatus(a.margin2, a.isMargin, 'noYes'),
			},
			{
				title: 'Initial Funding Amount < $5,000',
				status: getCheckStatus(a.initialFundingLessThanFiveThousand, true, 'noYes'),
			},
			{
				title: 'Lightspeed Trader OR Lightspeed Web and Mobile',
				status: getCheckStatus(a.tradingLightspeedTrader || a.tradingLightspeedWebMobile, true, 'noYes'),
			},
		],
	}),
	createRuleResult({
		title: 'Margin 1, Liquid Net Worth < $10,000, Total Net Worth < $10,000',
		applies: a.isMargin && a.margin1,
		blocksSTP: a.liquidNetWorthLessThanTenThousand || a.totalNetWorthLessThanTenThousand,
		checks: [
			{
				title: 'Margin 1',
				status: getCheckStatus(a.margin1, true, 'noYes'),
			},
			{
				title: 'Liquid Net Worth < $10,000',
				status: getCheckStatus(!a.liquidNetWorthLessThanTenThousand, a.isMargin && a.margin1, 'failPass'),
			},
			{
				title: 'Total Net Worth < $10,000',
				status: getCheckStatus(!a.totalNetWorthLessThanTenThousand, a.isMargin && a.margin1, 'failPass'),
			},
		],
	}),
	createRuleResult({
		title: 'Margin 1, Annual Income < $10,000, Liquid Net Worth < $25,000, Total Net Worth < $25,000',
		applies: a.isMargin && a.margin1 && a.annualIncomeLessThanTenThousand,
		blocksSTP: a.liquidNetWorthLessThanTwentyFiveThousand || a.totalNetWorthLessThanTwentyFiveThousand,
		checks: [
			{
				title: 'Margin 1',
				status: getCheckStatus(a.margin1, true, 'noYes'),
			},
			{
				title: 'Annual Income < $10,000',
				status: getCheckStatus(a.annualIncomeLessThanTenThousand, a.isMargin && a.margin1, 'noYes'),
			},
			{
				title: 'Liquid Net Worth < $25,000',
				status: getCheckStatus(
					!a.liquidNetWorthLessThanTwentyFiveThousand,
					a.isMargin && a.margin1 && a.annualIncomeLessThanTenThousand,
					'failPass',
				),
			},
			{
				title: 'Total Net Worth < $25,000',
				status: getCheckStatus(
					!a.totalNetWorthLessThanTwentyFiveThousand,
					a.isMargin && a.margin1 && a.annualIncomeLessThanTenThousand,
					'failPass',
				),
			},
		],
	}),
	createRuleResult({
		title: 'Margin 2, Liquid Net Worth < $25,000, Total Net Worth < $25,000',
		applies: a.isMargin && a.margin2,
		blocksSTP: a.liquidNetWorthLessThanTwentyFiveThousand || a.totalNetWorthLessThanTwentyFiveThousand,
		checks: [
			{
				title: 'Margin 2',
				status: getCheckStatus(a.margin2, true, 'noYes'),
			},
			{
				title: 'Liquid Net Worth < $25,000',
				status: getCheckStatus(!a.liquidNetWorthLessThanTwentyFiveThousand, a.isMargin && a.margin2, 'failPass'),
			},
			{
				title: 'Total Net Worth < $25,000',
				status: getCheckStatus(!a.totalNetWorthLessThanTwentyFiveThousand, a.isMargin && a.margin2, 'failPass'),
			},
		],
	}),
	createRuleResult({
		title: 'Margin 2, Annual Income < $25,000, Liquid Net Worth < $50,000, Total Net Worth < $50,000',
		applies: a.isMargin && a.margin2 && a.annualIncomeLessThanTwentyFiveThousand,
		blocksSTP: a.liquidNetWorthLessThanFiftyThousand || a.totalNetWorthLessThanFiftyThousand,
		checks: [
			{
				title: 'Margin 2',
				status: getCheckStatus(a.margin2, true, 'noYes'),
			},
			{
				title: 'Annual Income < $25,000',
				status: getCheckStatus(a.annualIncomeLessThanTwentyFiveThousand, a.isMargin && a.margin2, 'noYes'),
			},
			{
				title: 'Liquid Net Worth < $50,000',
				status: getCheckStatus(
					!a.liquidNetWorthLessThanFiftyThousand,
					a.isMargin && a.margin2 && a.annualIncomeLessThanTwentyFiveThousand,
					'failPass',
				),
			},
			{
				title: 'Total Net Worth < $50,000',
				status: getCheckStatus(
					!a.totalNetWorthLessThanFiftyThousand,
					a.isMargin && a.margin2 && a.annualIncomeLessThanTwentyFiveThousand,
					'failPass',
				),
			},
		],
	}),
	createRuleResult({
		title: 'Primary Applicant',
		applies: true,
		blocksSTP:
			(a.usPersonYes && a.usCitizenshipNo) ||
			a.usPersonNo ||
			a.firmAssociationYes ||
			a.brokerAssociationYes ||
			a.publicCompanyAssociationYes ||
			a.politicallyExposedYes ||
			a.usCurrentAddressNo ||
			(a.isMailingAddressDifferentFromCurrentAddress && a.usMailingAddressNo),
		checks: [
			{ title: 'US Person', status: getCheckStatus(!a.usPersonNo, true, 'failPass') },
			{ title: 'US Citizen', status: getCheckStatus(!a.usCitizenshipNo, a.usPersonYes, 'failPass') },
			{ title: 'US Current Address', status: getCheckStatus(!a.usCurrentAddressNo, true, 'failPass') },
			{
				title: 'US Mailing Address',
				status: getCheckStatus(!a.usMailingAddressNo, a.isMailingAddressDifferentFromCurrentAddress, 'failPass'),
			},
			{ title: 'Firm Association', status: getCheckStatus(!a.firmAssociationYes, true, 'failPass') },
			{ title: 'Broker Association', status: getCheckStatus(!a.brokerAssociationYes, true, 'failPass') },
			{
				title: 'Public Company Association',
				status: getCheckStatus(!a.publicCompanyAssociationYes, true, 'failPass'),
			},
			{
				title: 'Account Politically Exposed',
				status: getCheckStatus(!a.politicallyExposedYes, true, 'failPass'),
			},
		],
	}),
	createRuleResult({
		title: 'Joint Applicant',
		applies: a.isJoint,
		blocksSTP:
			a.jointFirmAssociationYes ||
			a.jointBrokerAssociationYes ||
			a.jointPublicCompanyAssociationYes ||
			(a.jointUsPersonYes && a.jointUsCitizenshipNo) ||
			a.jointCurrentAddressNo ||
			a.jointUsPersonNo ||
			a.jointAccountPoliticallyExposedYes ||
			(a.isJointMailingAddressDifferentFromCurrentAddress && a.jointMailingAddressNo),
		checks: [
			{ title: 'US Person', status: getCheckStatus(!a.jointUsPersonNo, a.isJoint, 'failPass') },
			{
				title: 'US Citizen',
				status: getCheckStatus(!a.jointUsCitizenshipNo, a.isJoint && a.jointUsPersonYes, 'failPass'),
			},
			{ title: 'US Current Address', status: getCheckStatus(!a.jointCurrentAddressNo, a.isJoint, 'failPass') },
			{
				title: 'US Mailing Address',
				status: getCheckStatus(
					!a.jointMailingAddressNo,
					a.isJoint && a.isJointMailingAddressDifferentFromCurrentAddress,
					'failPass',
				),
			},
			{ title: 'Firm Association', status: getCheckStatus(!a.jointFirmAssociationYes, a.isJoint, 'failPass') },
			{ title: 'Broker Association', status: getCheckStatus(!a.jointBrokerAssociationYes, a.isJoint, 'failPass') },
			{
				title: 'Public Company Association',
				status: getCheckStatus(!a.jointPublicCompanyAssociationYes, a.isJoint, 'failPass'),
			},
			{
				title: 'Account Politically Exposed',
				status: getCheckStatus(!a.jointAccountPoliticallyExposedYes, a.isJoint, 'failPass'),
			},
		],
	}),
	createRuleResult({
		title: 'Cash Account with Sterling Trader Pro, DAS Trader Pro, RealTick, or Silexx',
		applies: a.isCash,
		blocksSTP:
			a.tradingPlatformSterlingTraderPro ||
			a.tradingPlatformDastraderPro ||
			a.tradingPlatformRealtick ||
			a.tradingPlatformSilexx,
		checks: [
			{ title: 'Cash Account', status: getCheckStatus(a.isCash, true, 'noYes') },
			{
				title: 'Sterling Trader Pro',
				status: getCheckStatus(!a.tradingPlatformSterlingTraderPro, a.isCash, 'failPass'),
			},
			{
				title: 'DAS Trader Pro',
				status: getCheckStatus(!a.tradingPlatformDastraderPro, a.isCash, 'failPass'),
			},
			{ title: 'RealTick', status: getCheckStatus(!a.tradingPlatformRealtick, a.isCash, 'failPass') },
			{ title: 'Silexx', status: getCheckStatus(!a.tradingPlatformSilexx, a.isCash, 'failPass') },
		],
	}),
	createRuleResult({
		title: 'LightSpeed Connect, Sterling Trader Pro, DAS Trader Pro, RealTick, or Silexx',
		applies: true,
		blocksSTP:
			a.tradingPlatformLightspeedConnect ||
			a.tradingPlatformSterlingTraderPro ||
			a.tradingPlatformDastraderPro ||
			a.tradingPlatformRealtick ||
			a.tradingPlatformSilexx,
		checks: [
			{
				title: 'LightSpeed Connect',
				status: getCheckStatus(!a.tradingPlatformLightspeedConnect, true, 'failPass'),
			},
			{
				title: 'Sterling Trader Pro',
				status: getCheckStatus(!a.tradingPlatformSterlingTraderPro, true, 'failPass'),
			},
			{
				title: 'DAS Trader Pro',
				status: getCheckStatus(!a.tradingPlatformDastraderPro, true, 'failPass'),
			},
			{ title: 'RealTick', status: getCheckStatus(!a.tradingPlatformRealtick, true, 'failPass') },
			{ title: 'Silexx', status: getCheckStatus(!a.tradingPlatformSilexx, true, 'failPass') },
		],
	}),
	createRuleResult({
		title: 'Do you plan to trade options in this account?',
		applies: a.tradeOptions,
		blocksSTP: a.investmentObjectivesCapital,
		checks: [
			{ title: 'Do you plan to trade options in this account?', status: getCheckStatus(a.tradeOptions, true, 'noYes') },
			{
				title: '"Your Investment Objectives" =  ONLY Conservation of Capital',
				status: getCheckStatus(!a.investmentObjectivesCapital, a.tradeOptions, 'failPass'),
			},
		],
	}),
	createRuleResult({
		title: 'Margin AND Your Investment Objectives" = ONLY Conservation of Capital',
		applies: a.isMargin,
		blocksSTP: a.investmentObjectivesCapital,
		checks: [
			{ title: 'Margin Account', status: getCheckStatus(a.isMargin, true, 'noYes') },
			{
				title: '"Your Investment Objectives" =  ONLY Conservation of Capital',
				status: getCheckStatus(!a.investmentObjectivesCapital, a.isMargin, 'failPass'),
			},
		],
	}),
	createRuleResult({
		title: 'Option Level 2',
		applies: checkOptionLevelTwo(a),
		blocksSTP:
			a.isMargin &&
			(a.annualIncomeLessThanTwentyFiveThousand ||
				a.totalNetWorthLessThanTwentyFiveThousand ||
				a.liquidNetWorthLessThanTwentyFiveThousand),
		checks: [
			{ title: 'Option Level 2', status: getCheckStatus(checkOptionLevelTwo(a), true, 'noYes') },
			{ title: 'Margin Account', status: getCheckStatus(a.isMargin, true, 'noYes') },
			{
				title: 'Annual Income < $25000',
				status: getCheckStatus(
					!a.annualIncomeLessThanTwentyFiveThousand,
					a.isMargin && checkOptionLevelTwo(a),
					'failPass',
				),
			},
			{
				title: 'Total Net Worth < $25000',
				status: getCheckStatus(
					!a.totalNetWorthLessThanTwentyFiveThousand,
					a.isMargin && checkOptionLevelTwo(a),
					'failPass',
				),
			},
			{
				title: 'Liquid Net Worth < $25000',
				status: getCheckStatus(
					!a.liquidNetWorthLessThanTwentyFiveThousand,
					a.isMargin && checkOptionLevelTwo(a),
					'failPass',
				),
			},
		],
	}),
	createRuleResult({
		title: 'Option Level 3',
		applies: checkOptionLevelThree(a),
		blocksSTP:
			a.annualIncomeLessThanTwentyFiveThousand ||
			a.totalNetWorthLessThanTwentyFiveThousand ||
			a.liquidNetWorthLessThanTwentyFiveThousand,
		checks: [
			{ title: 'Option Level 3', status: getCheckStatus(checkOptionLevelThree(a), true, 'noYes') },
			{
				title: 'Annual Income < $25000',
				status: getCheckStatus(!a.annualIncomeLessThanTwentyFiveThousand, checkOptionLevelThree(a), 'failPass'),
			},
			{
				title: 'Total Net Worth < $25000',
				status: getCheckStatus(!a.totalNetWorthLessThanTwentyFiveThousand, checkOptionLevelThree(a), 'failPass'),
			},
			{
				title: 'Liquid Net Worth < $25000',
				status: getCheckStatus(!a.liquidNetWorthLessThanTwentyFiveThousand, checkOptionLevelThree(a), 'failPass'),
			},
		],
	}),
	createRuleResult({
		title: 'Option Level 4',
		applies: checkOptionLevelFour(a),
		blocksSTP:
			!checkOptionLevelFourSpeculation(a) ||
			!a.riskToleranceHighRisk ||
			a.annualIncomeLessThanFortyThousand ||
			a.liquidNetWorthLessThanFortyThousand ||
			a.totalNetWorthLessThanSixtyFiveThousand ||
			a.initialFundingLessThanTwentyFiveThousand,
		checks: [
			{ title: 'Option Level 4', status: getCheckStatus(checkOptionLevelFour(a), true, 'noYes') },
			{
				title: 'Speculation is one of my investment objectives.',
				status: getCheckStatus(checkOptionLevelFourSpeculation(a), checkOptionLevelFour(a), 'failPass'),
			},
			{
				title: 'Risk Tolerance is High Risk.',
				status: getCheckStatus(a.riskToleranceHighRisk, checkOptionLevelFour(a), 'failPass'),
			},
			{
				title: 'Annual Income < $40000',
				status: getCheckStatus(!a.annualIncomeLessThanFortyThousand, checkOptionLevelFour(a), 'failPass'),
			},
			{
				title: 'Liquid Net Worth < $40000',
				status: getCheckStatus(!a.liquidNetWorthLessThanFortyThousand, checkOptionLevelFour(a), 'failPass'),
			},
			{
				title: 'Total Net Worth < $65000',
				status: getCheckStatus(!a.totalNetWorthLessThanSixtyFiveThousand, checkOptionLevelFour(a), 'failPass'),
			},
			{
				title: 'Initial Funding < $25000',
				status: getCheckStatus(!a.initialFundingLessThanTwentyFiveThousand, checkOptionLevelFour(a), 'failPass'),
			},
		],
	}),
	createRuleResult({
		title: 'Option Level 5',
		applies: a.isMargin && checkOptionLevelFive(a),
		blocksSTP:
			!checkOptionLevelFiveSpeculation(a) ||
			!a.riskToleranceHighRisk ||
			a.annualIncomeLessThanEightyThousand ||
			a.liquidNetWorthLessThanOneHundredThousand ||
			a.totalNetWorthLessThanOneHundredFiftyThousand ||
			a.initialFundingLessThanSeventyFiveThousand,
		checks: [
			{ title: 'Option Level 5', status: getCheckStatus(checkOptionLevelFive(a), true, 'noYes') },
			{
				title: 'Speculation is one of my investment objectives.',
				status: getCheckStatus(checkOptionLevelFiveSpeculation(a), checkOptionLevelFive(a), 'failPass'),
			},
			{
				title: 'Risk Tolerance is High Risk.',
				status: getCheckStatus(a.riskToleranceHighRisk, checkOptionLevelFive(a), 'failPass'),
			},
			{
				title: 'Annual Income < $80,000',
				status: getCheckStatus(!a.annualIncomeLessThanEightyThousand, checkOptionLevelFive(a), 'failPass'),
			},
			{
				title: 'Liquid Net Worth < $10,0000',
				status: getCheckStatus(!a.liquidNetWorthLessThanOneHundredThousand, checkOptionLevelFive(a), 'failPass'),
			},
			{
				title: 'Total Net Worth < $150,000',
				status: getCheckStatus(!a.totalNetWorthLessThanOneHundredFiftyThousand, checkOptionLevelFive(a), 'failPass'),
			},
			{
				title: 'Initial Funding < $75,000',
				status: getCheckStatus(!a.initialFundingLessThanSeventyFiveThousand, checkOptionLevelFive(a), 'failPass'),
			},
		],
	}),
];

const lightSpeedSTPCheckDetailed = (): STPCheckResult => {
	const questions = store.getState().applicationForm.questions;
	const answers = getLightSpeedFormAnswers(questions);
	const rules = getLightSpeedRuleResults(answers);

	return {
		isAllowed: rules.every(rule => rule.status !== 'fail'),
		rules,
	};
};

export const lightSpeedSTPCheck = (applicationCreatedAt: string): boolean => {
	if (
		process.env.DEPLOYMENT_ENVIRONMENT === 'production' &&
		new Date(applicationCreatedAt).getTime() < JUNE_1_2026_4PM_EASTERN_MS
	) {
		return false;
	}
	return lightSpeedSTPCheckDetailed().isAllowed;
};

const isSTPAllowed = (applicationCreatedAt: string): boolean => {
	switch (environment.env.FIRM_ENVIRONMENT) {
		case 'lightspeed':
			return lightSpeedSTPCheck(applicationCreatedAt);
		case 'kingswood':
		case 'usmart':
			return false;
		default:
			return true;
	}
};

const checkSTPDetailed = (): STPCheckResult => {
	switch (environment.env.FIRM_ENVIRONMENT) {
		case 'lightspeed':
			return lightSpeedSTPCheckDetailed();
		case 'kingswood':
		case 'usmart':
			return {
				isAllowed: false,
				rules: [
					{
						title: 'STP is not supported for this firm environment',
						status: 'fail',
						checks: [{ title: 'firmSupportsSTP', status: 'no' }],
					},
				],
			};
		default:
			return {
				isAllowed: true,
				rules: [
					{
						title: 'No STP rules apply for this firm environment',
						status: 'na',
						checks: [{ title: 'firmUsesLightSpeedRules', status: 'na' }],
					},
				],
			};
	}
};

function useSubmitApplicationForm({
	applicationId,
	formId,
	formVersion,
	runBackground,
	applicationCreatedAt,
}: {
	applicationId: number;
	formId: number;
	formVersion?: number | null;
	runBackground: boolean;
	applicationCreatedAt: string;
}) {
	const history = useHistory();
	const generateRoutePath = useGenerateRoutePath();

	const { isPending: isSubmitting, startRequest: submitApplicationForm } = useTriggeredRequest(
		AccountClientRequests.Applications.submitApplicationForm,
	);

	const { isPending: isBackgroundCheck, startRequest: backgroundCheckApplicationForm } = useTriggeredRequest(
		AccountClientRequests.Applications.backgroundCheckApplicationForm,
	);

	const { isPending: isVerifyDocument, startRequest: verifyDocumentApplicationForm } = useTriggeredRequest(
		AccountClientRequests.Applications.verifyDocumentApplicationForm,
	);

	const { isPending: isCustodianCheck, startRequest: custodianCheckApplicationForm } = useTriggeredRequest(
		AccountClientRequests.Applications.custodianCheckApplicationForm,
	);

	const { fetchApplicationForm, isLoading: isCustodianCompleting } = useFetchApplicationForm();

	const checkSTPAllowed = useCallback((): STPCheckResult => checkSTPDetailed(), []);

	const handleSubmitApplicationForm = useCallback(async () => {
		try {
			await submitApplicationForm({ application_id: applicationId });

			showToast.success('Form has been submitted successfully');

			if (environment.env.FIRM_ENVIRONMENT === 'bosonic' || environment.env.FIRM_ENVIRONMENT === 'secondlane') {
				const token = store.getState().auth.tokens?.access_token;
				if (environment.env.BUILD_ENVIRONMENT === 'production') {
					window.open(`https://onboarding.bosonic.digital/signing/login?id=${applicationId}&t=${token}`, '_blank');
				} else {
					window.open(
						`https://onboarding-qa.bosonicnetwork.com/signing/login?id=${applicationId}&t=${token}`,
						'_blank',
					);
				}
			}

			if (runBackground) {
				try {
					const {
						application: { applicants },
					} = await backgroundCheckApplicationForm({ application_id: applicationId });

					const backgroundCheckResults = await Promise.all(
						applicants
							.filter(
								({ latest_background_check }) =>
									latest_background_check.status !== 'success' &&
									latest_background_check.response?.check_status === 'DOCUMENTATION_REQUIRED',
							)
							.map(async applicant => {
								const res = await verifyDocumentApplicationForm({
									application_id: applicationId,
									applicant_type: applicant.type,
								});

								return checkVerifyDocuments({ applicantType: applicant.type, applicants: res.application.applicants });
							}),
					);

					const isBackgroundCheckPassed = backgroundCheckResults.every(Boolean);

					if (isBackgroundCheckPassed) showToast.success('Background check has been passed successfully');

					if (isBackgroundCheckPassed && isSTPAllowed(applicationCreatedAt)) {
						const res = await custodianCheckApplicationForm({ application_id: applicationId });

						if (res.application.current_custodian_request.status === 'success' && isEnableSSO()) {
							await fetchApplicationForm({ applicationId, formId, formVersion });
						}
					}
				} catch (e) {
					//showToast.error('Background check failed.');
				}
			}

			history.push(generateRoutePath(RoutePaths.MAIN.APPLICATIONS));
		} catch (e) {
			showToast.error('Form contains errors that need to be fixed before the application can be submitted');
		}
	}, [
		applicationCreatedAt,
		applicationId,
		backgroundCheckApplicationForm,
		custodianCheckApplicationForm,
		fetchApplicationForm,
		formId,
		formVersion,
		generateRoutePath,
		history,
		runBackground,
		submitApplicationForm,
		verifyDocumentApplicationForm,
	]);

	return {
		isSubmitting: isSubmitting || isBackgroundCheck || isVerifyDocument || isCustodianCheck || isCustodianCompleting,
		handleSubmitApplicationForm,
		checkSTPAllowed,
	};
}

export default useSubmitApplicationForm;
